import { z } from 'zod';
import { EngagementStatus } from './engagement.js';
import { dayAfter } from './firm-day.js';
import { isoDay, isoInstant } from './values.js';
/**
 * The life of a contract after its acceptance (ADR-071 § 9 stage 6, STBV-E B.2): in force from
 * its start, supplemented by an addendum that replaces it, terminated by either side with the
 * notice period of the terms (E4.KUEND.*), ended on the effective day - and then the return of
 * the documents the firm keeps (§ 66 StBerG).
 */
/** The first version of a contract, or an addendum to the version in force (E5.LZ.020-030). */
export const EngagementVersionKind = z
    .enum(['initial', 'addendum'])
    .meta({ id: 'EngagementVersionKind' });
/** Who gave notice: the firm, or the client. */
export const TerminationParty = z.enum(['firm', 'client']).meta({ id: 'TerminationParty' });
export const terminationPartyLabel = {
    firm: 'Kanzlei',
    client: 'Mandant',
};
/**
 * Ordinary notice with the period of the terms (E4.KUEND.030), or without notice - for cause or,
 * for services of a higher kind, at any time (§ 627 BGB, E4.KUEND.070).
 */
export const TerminationKind = z.enum(['ordinary', 'for_cause']).meta({ id: 'TerminationKind' });
export const terminationKindLabel = {
    ordinary: 'Ordentliche Kündigung',
    for_cause: 'Fristlose Kündigung (wichtiger Grund, § 627 BGB)',
};
/**
 * How the notice came: the client through the portal, the firm in its app, the client's notice
 * the firm received by other means and recorded - or the end of the mandate (ADR-063).
 */
export const TerminationChannel = z
    .enum(['portal', 'firm', 'recorded', 'mandate_end'])
    .meta({ id: 'TerminationChannel' });
export const terminationChannelLabel = {
    portal: 'Im Mandantenportal',
    firm: 'Durch die Kanzlei',
    recorded: 'Auf anderem Weg eingegangen, von der Kanzlei erfasst',
    mandate_end: 'Mit dem Mandatsende',
};
/**
 * The notice period where the terms name none (E4.KUEND.030: four weeks, the templates' value) -
 * the firm sets its own in the catalogue (`FirmStandards.noticePeriod`).
 */
export const DEFAULT_NOTICE_PERIOD = { amount: 4, unit: 'week' };
/** A notice period in words: „4 Wochen“, „1 Monat“. */
export const noticePeriodText = ({ amount, unit }) => `${String(amount)} ${unit === 'week' ? (amount === 1 ? 'Woche' : 'Wochen') : amount === 1 ? 'Monat' : 'Monate'}`;
/** The last day of a month, `month` 1-12. */
const lastDayOf = (year, month) => new Date(Date.UTC(year, month, 0)).getUTCDate();
const pad = (n) => String(n).padStart(2, '0');
/**
 * The last day of a notice received on `receivedOn` (§§ 187 Abs. 1, 188 Abs. 2, 3 BGB): the day
 * of receipt not counted, the period ends with the day of the last week or month that bears the
 * receipt's weekday or number - or, where the month has no such day, with its last day. The
 * contract ends with the end of that day in Europe/Berlin.
 */
export function noticeLastDay(receivedOn, period) {
    if (period.unit === 'week') {
        let day = receivedOn;
        for (let n = 0; n < period.amount * 7; n += 1)
            day = dayAfter(day);
        return day;
    }
    const [year = 0, month = 1, date = 1] = receivedOn.split('-').map(Number);
    const months = month - 1 + period.amount;
    const toYear = year + Math.floor(months / 12);
    const toMonth = (months % 12) + 1;
    const toDate = Math.min(date, lastDayOf(toYear, toMonth));
    return `${String(toYear)}-${pad(toMonth)}-${pad(toDate)}`;
}
/**
 * The day a notice takes effect: for cause, the day it was received; otherwise the end of the
 * notice period - or a later day the notice names („zum 31.12.“). `null`: the day named lies
 * before the end of the period.
 */
export function terminationLastDay(notice) {
    if (notice.kind === 'for_cause')
        return notice.endsOn ?? notice.receivedOn;
    const earliest = noticeLastDay(notice.receivedOn, notice.period);
    if (notice.endsOn === null)
        return earliest;
    return notice.endsOn >= earliest ? notice.endsOn : null;
}
/**
 * Until when the firm keeps the files of the mandate (§ 66 Abs. 1 StBerG): ten years from the
 * end of the calendar year in which the engagement ended.
 */
export const fileRetentionUntil = (endedOn) => `${String(Number(endedOn.slice(0, 4)) + 10)}-12-31`;
/** How long the client has to collect the documents once asked to (§ 66 Abs. 2 StBerG). */
export const PICKUP_MONTHS = 6;
const version = z.int().positive();
const reason = z.string().trim().min(1).max(1000).nullable();
/**
 * Notice recorded or given by the firm (E4.KUEND.030, text form): the firm's own, or the
 * client's received by letter, mail or otherwise on `receivedOn` - not after today. `endsOn`: a
 * later day the notice names; `null` the end of the period, for cause the day received.
 */
export const EngagementTerminationInput = z
    .object({
    version,
    party: TerminationParty,
    receivedOn: isoDay,
    kind: TerminationKind,
    endsOn: isoDay.nullable(),
    reason,
})
    .strict()
    .meta({ id: 'EngagementTerminationInput' });
/** The client's notice through the portal: name and address for the confirmation (§ 312k BGB). */
export const OwnTerminationInput = z
    .object({
    version,
    kind: TerminationKind,
    endsOn: isoDay.nullable(),
    name: z.string().trim().min(2).max(200),
    email: z.email().max(254),
    reason,
})
    .strict()
    .meta({ id: 'OwnTerminationInput' });
/** A notice as kept with the version in force: when it came, from whom, to which day. */
export const EngagementTermination = z
    .object({
    receivedAt: isoInstant,
    party: TerminationParty,
    channel: TerminationChannel,
    /** `null`: not a notice, but the end of the mandate (ADR-063). */
    kind: TerminationKind.nullable(),
    /** The last day of the contract. */
    endsOn: isoDay,
    reason: z.string().nullable(),
    name: z.string().nullable(),
    email: z.string().nullable(),
    /** Who recorded it: the portal account, the firm's person, or Kanos. */
    recordedBy: z.string(),
})
    .meta({ id: 'EngagementTermination' });
/**
 * The contract that applies - in force, terminated or ended - with its life: since when, the
 * notice and its effect, and after the end how long the firm keeps the files.
 */
export const RunningContract = z
    .object({
    version,
    status: EngagementStatus,
    inForceFrom: isoDay,
    /** The agreed period of notice. */
    noticePeriod: z.object({ amount: z.int(), unit: z.enum(['week', 'month']) }),
    /** The last day an ordinary notice received today would end the contract. */
    earliestEnd: isoDay,
    termination: EngagementTermination.nullable(),
    endedAt: isoInstant.nullable(),
    /** After the end: the files are kept until (§ 66 Abs. 1 StBerG). */
    retentionUntil: isoDay.nullable(),
    /** Whether notice may be given now. */
    terminable: z.boolean(),
})
    .meta({ id: 'RunningContract' });
/** A document of a version as the client sees it. */
export const OfferedDocument = z.object({ id: z.string(), title: z.string(), byteSize: z.int() });
/** The running contract as the client sees it in the portal, with its documents. */
export const OwnRunningContract = RunningContract.extend({
    firmName: z.string(),
    clientName: z.string(),
    documents: z.array(OfferedDocument),
}).meta({ id: 'OwnRunningContract' });
//# sourceMappingURL=engagement-lifecycle.js.map